import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Controller, FormProvider, SubmitErrorHandler, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import toast from "react-hot-toast";
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
} from "react-bootstrap";
import { useTheme } from "styled-components";
import { v4 } from "uuid";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { useProperty } from "../../../components/property-provider";
import { SectionHeading } from "../../../components/section-heading";
import ContentWrapper from "../../../components/content-wrapper";
import { ErrorField } from "../../shared/widgets/TranslationComponent/styles";
import {
  createEmptyExperiment,
  ExperimentEntity,
  ExperimentInput,
  ExperimentListItem,
  VariantType,
} from "./domain/experiment.entity";
import { createExperimentFormSchema } from "./components/experimentCreateForm.schema";
import { useHotelTimeZone } from "./application/hooks/useHotelTimeZone";
import { ExperimentGrpcRepository } from "./infrastructure/repositories/ExperimentGrpcRepository";
import { useExperimentMutations } from "./application/hooks/useExperimentMutations";
import { ScenarioGrpcRepository } from "../scenario/infrastructure/repositories/ScenarioGrpcRepository";
import { ScenarioEntity } from "../scenario/domain/scenario.entity";
import { useConditionTypeLabels } from "../orchestrator.messages";
import { messages } from "./experiment.messages";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  formatComparabilityDetail,
  ScenarioSelectCard,
  selectionIdentity,
} from "./components/ScenarioSelectCard";
import { ComparabilityResult, parseComparabilityError } from "./domain/scenarioComparability";
import { mapGenericCreateExperimentError } from "./domain/createExperimentError.helper";
import { DateField } from "./components/DateField";
import {
  DEFAULT_BASELINE_CONVERSION_RATE,
  roundedRequiredValidVisitors,
} from "./domain/sampleSize";

const scenarioRepository = new ScenarioGrpcRepository();
const experimentRepository = new ExperimentGrpcRepository();

const DATE_VALIDATION_MESSAGES = {
  startDatePast: messages.validation.startDatePast,
  endBeforeStart: messages.validation.endBeforeStart,
} as const;

function isDateValidationKey(
  message: string | undefined
): message is keyof typeof DATE_VALIDATION_MESSAGES {
  return !!message && message in DATE_VALIDATION_MESSAGES;
}

/**
 * A missing `startedAt` fails `ExperimentSchema`'s own `z.date()` field (a native "invalid_type"
 * issue) before `experimentCreateForm.schema.ts`'s `.superRefine` ever runs - mirroring how
 * `ticker`'s required-ness is already detected via `errors.ticker.type === "too_small"` above.
 */
function isStartDateRequiredError(error: { type?: string } | undefined): boolean {
  return error?.type === "invalid_type";
}

export function ExperimentEdit() {
  const intl = useIntl();
  const history = useHistory();
  const propertyInfo = useProperty();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState<ScenarioEntity[]>([]);
  const [existingExperiments, setExistingExperiments] = useState<ExperimentListItem[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  // Corrective safety-net notice for the backend's V7 comparability rejection (US-014) — a
  // different, backend-error-driven mechanism from the preventive per-option disabling below, not
  // a revival of US-012's removed `scenarioValidationError` alert.
  const [comparabilityError, setComparabilityError] = useState<ComparabilityResult | null>(null);
  // "Both sides must have a scenario selected" notice (this story, US-016) - a different check
  // from comparabilityError above: this one fires when a side has no selection at all, not when
  // two selected sides are incompatible with each other.
  const [scenarioSelectionError, setScenarioSelectionError] = useState(false);
  const conditionTypeLabels = useConditionTypeLabels();

  const { create } = useExperimentMutations(propertyInfo.ticker, experimentRepository);

  const hotelTimeZone = useHotelTimeZone();
  const experimentFormSchema = useMemo(() => createExperimentFormSchema(hotelTimeZone), [
    hotelTimeZone,
  ]);

  const form = useForm<ExperimentInput, unknown, ExperimentEntity>({
    resolver: zodResolver(experimentFormSchema),
    defaultValues: createEmptyExperiment(v4()),
  });

  const watchedVariants = form.watch("variants");
  const watchedMde = form.watch("mde");

  // Live, pre-creation estimate of the sample the configured MDE will need. The authoritative
  // figure is `required_visitors` on the results response once the experiment exists — this is a
  // sizing aid, and the copy says so (see domain/sampleSize.ts).
  const estimatedVisitors = useMemo(() => roundedRequiredValidVisitors(Number(watchedMde) || 0), [
    watchedMde,
  ]);

  // The traffic split is deliberately not exposed in this version: `createEmptyExperiment` seeds
  // both variants at 50, nothing ever writes another value, and so every submission carries the
  // 50/50 the backend (and its SRM check) still expects. `ExperimentSchema`'s sum-to-100
  // refinement remains the guard for any value arriving by another route.

  // Load scenarios and existing experiments on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [scenariosData, experimentsData] = await Promise.all([
          scenarioRepository.findByProperty(propertyInfo.ticker),
          experimentRepository.list({}),
        ]);
        setScenarios(scenariosData);
        setExistingExperiments(experimentsData);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [propertyInfo.ticker]);

  // Get scenario IDs that are already in use
  const scenariosInUse = useMemo(() => {
    const usedScenarioIds = new Set<string>();
    existingExperiments.forEach(item => {
      item.experiment.variants.forEach(variant => {
        if (variant.scenarioId) {
          usedScenarioIds.add(variant.scenarioId);
        }
      });
    });
    return usedScenarioIds;
  }, [existingExperiments]);

  // Available scenarios (not in use by other experiments)
  const availableScenarios = useMemo(() => {
    return scenarios.filter(s => !scenariosInUse.has(s.id));
  }, [scenarios, scenariosInUse]);

  // Identity (scenario id or "__ENGINE_DEFAULT__") currently selected on each side, used to
  // compute the opposite side's disabled option via mutual exclusion.
  const sideASelection = selectionIdentity(watchedVariants?.[0]);
  const sideBSelection = selectionIdentity(watchedVariants?.[1]);

  // Opposite-side selection info passed to Scenario B's ScenarioSelectCard for comparability-based
  // disabling (US-014) — `undefined` when side A has no selection yet, so that no comparability-
  // based disabling applies (Business Rule 5). Comparability filtering is intentionally one-way:
  // Scenario A (the control) is never narrowed by what's picked on B — it always offers every
  // available scenario. Only Scenario B's options are filtered down to what's comparable with A's
  // current selection.
  const sideBOppositeVariant =
    sideASelection === null
      ? undefined
      : {
          scenarioId: watchedVariants?.[0]?.scenarioId ?? null,
          isEngineDefault: !!watchedVariants?.[0]?.isEngineDefault,
        };

  // Scenario B's selection is only ever meaningful relative to Scenario A's current criteria, so
  // whenever Scenario A changes (including being cleared), Scenario B's own prior selection is
  // reset - forcing the user to re-pick a (now-valid) comparable scenario on that side, rather than
  // silently keeping a stale/incomparable value in the submitted form. Skipped on mount (the ref
  // starts equal to the initial value) so an empty Scenario A doesn't wipe anything on first render.
  const prevSideASelectionRef = useRef(sideASelection);
  useEffect(() => {
    if (prevSideASelectionRef.current !== sideASelection) {
      form.setValue("variants.1.scenarioId", null, { shouldValidate: true, shouldDirty: true });
      form.setValue("variants.1.scenarioTicker", null);
      form.setValue("variants.1.isEngineDefault", false);
      form.setValue("variants.1.type", VariantType.VARIANT_TYPE_SCENARIO);
      form.setValue("variants.1.url", null);
      prevSideASelectionRef.current = sideASelection;
    }
  }, [sideASelection, form]);

  const handleGoBack = useCallback(() => {
    history.push(`/${propertyInfo.ticker}/orchestrator/experiments`);
  }, [history, propertyInfo.ticker]);

  // "A side has a selection" is defined identically to US-012's own definition:
  // `selectionIdentity(variant) !== null` (a real scenarioId or the isEngineDefault sentinel).
  // Both handleInvalidSubmit (below) and handleSubmit's own defensive guard use this exact same
  // predicate - see Business Rules item 2 in
  // openspec/changes/archive/2026-09-02-us-016-creation-submission-and-redirect/proposal.md.
  const handleInvalidSubmit: SubmitErrorHandler<ExperimentInput> = useCallback(
    errors => {
      const sideAUnselected =
        !!errors.variants?.[0]?.ticker && selectionIdentity(watchedVariants?.[0]) === null;
      const sideBUnselected =
        !!errors.variants?.[1]?.ticker && selectionIdentity(watchedVariants?.[1]) === null;
      // Only treat this as "no scenario selected" when the ticker error coincides with an
      // actually unselected side (VariantSchema.ticker is unconditionally required, so an
      // unselected side always fails Zod validation on an invisible, unmounted field). A real
      // "name is blank" error on an already-selected side is left to react-hook-form's own
      // per-field ErrorField rendering, unchanged from US-012.
      if (sideAUnselected || sideBUnselected) {
        setScenarioSelectionError(true);
      }
    },
    [watchedVariants]
  );

  const handleSubmit = useCallback(
    async (data: ExperimentEntity) => {
      setScenarioSelectionError(false);

      const sideASelected = selectionIdentity(data.variants[0]) !== null;
      const sideBSelected = selectionIdentity(data.variants[1]) !== null;
      if (!sideASelected || !sideBSelected) {
        // Reachable in principle (e.g. a future relaxation of VariantSchema.ticker), kept as a
        // defensive mirror of handleInvalidSubmit's check.
        setScenarioSelectionError(true);
        return;
      }

      try {
        setLoading(true);
        const { id, status, createdAt, updatedAt, deletedAt, ...createInput } = data;
        await create(createInput);
        // Redirect to the experiment list - not the results page, since a brand-new experiment
        // has no results to show yet (this story's one deliberate behavior change).
        history.push(`/${propertyInfo.ticker}/orchestrator/experiments`);
      } catch (error) {
        // Corrective safety net (US-014): the preventive per-option disabling is designed to make
        // this branch practically unreachable, but stale data / a race condition can still let an
        // invalid pair reach the backend's V7 comparability check (INVALID_ARGUMENT, code 3).
        // Checked first - only when it returns null does any other rejection fall through to the
        // generic mapped-message toast (this story, US-016).
        const parsed = parseComparabilityError(error);
        if (parsed) {
          setComparabilityError(parsed);
        } else {
          // Backend error toast: the client-side rules mirror most of the backend's own
          // validation, so this branch is a residual safety net (e.g. a ticker uniqueness race,
          // a stale scenario, or an unexpected server error), not the primary defense. The form
          // is deliberately not reset here so the user can correct and resubmit.
          toast.error(intl.formatMessage(mapGenericCreateExperimentError(error)));
        }
        setLoading(false);
      }
    },
    [create, propertyInfo.ticker, history, intl]
  );

  if (loadingData) {
    return (
      <ContentWrapper>
        <SectionHeading>
          <FormattedMessage {...messages.newExperiment} />
        </SectionHeading>
        <Container className="p-4" fluid={true}>
          <div>
            <FormattedMessage {...messages.results.loading} />
          </div>
        </Container>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...messages.newExperiment} />
      </SectionHeading>
      <Container className="p-4" fluid={true}>
        <FormProvider {...(form as any)}>
          <Form
            onSubmit={event => {
              // Cleared here, at the one shared point every submit attempt passes through
              // before react-hook-form's `handleSubmit` branches into either `onValid`
              // (handleSubmit) or `onInvalid` (handleInvalidSubmit) - not only inside a
              // successful `onValid` run, so a stale comparability notice from a prior
              // attempt can never linger into a later, differently-failing attempt.
              setComparabilityError(null);
              return form.handleSubmit(handleSubmit, handleInvalidSubmit)(event);
            }}>
            {/* Block 1: Identify the experiment */}
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">
                  <FormattedMessage {...messages.basicData} />
                </h5>

                <div className="row">
                  {/* Experiment Ticker */}
                  <div className="col-md-6">
                    <FormGroup className="mb-3">
                      <FormLabel htmlFor="tickerInput">
                        <FormattedMessage {...messages.formLabels.ticker} />
                      </FormLabel>
                      <FormControl
                        id="tickerInput"
                        type="text"
                        placeholder={intl.formatMessage(messages.formLabels.tickerPlaceholder)}
                        {...form.register("ticker")}
                      />
                      <Form.Text muted>
                        <FormattedMessage {...messages.formLabels.tickerHelp} />
                      </Form.Text>
                      {form.formState.errors.ticker && (
                        <ErrorField>
                          {form.formState.errors.ticker.type === "too_small"
                            ? intl.formatMessage(messages.validation.tickerRequired)
                            : form.formState.errors.ticker.message}
                        </ErrorField>
                      )}
                    </FormGroup>
                  </div>

                  {/* Experiment Description */}
                  <div className="col-md-6">
                    <FormGroup className="mb-3">
                      <FormLabel htmlFor="descriptionInput">
                        <FormattedMessage {...messages.formLabels.description} />
                      </FormLabel>
                      <Form.Control
                        id="descriptionInput"
                        type="text"
                        maxLength={500}
                        placeholder={intl.formatMessage(messages.formLabels.descriptionPlaceholder)}
                        {...form.register("description")}
                      />
                      {form.formState.errors.description && (
                        <ErrorField>{form.formState.errors.description.message}</ErrorField>
                      )}
                    </FormGroup>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Scenario Selection Section */}
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">
                  <FormattedMessage {...messages.scenarioSelection} />
                </h5>
                <p className="text-muted">
                  <FormattedMessage {...messages.scenarioSelectionHelp} />
                </p>

                {scenarioSelectionError && (
                  <Alert variant="danger">
                    <FormattedMessage {...messages.validation.scenariosRequired} />
                  </Alert>
                )}

                {comparabilityError && !comparabilityError.comparable && (
                  <Alert variant="danger" className="mt-2">
                    {formatComparabilityDetail(comparabilityError, conditionTypeLabels, intl)}
                  </Alert>
                )}

                <div className="row">
                  {/* Control Variant (Variant 0) */}
                  <div className="col-md-6">
                    <Card className="mb-3">
                      <Card.Body>
                        <ScenarioSelectCard
                          variantIndex={0}
                          titleMessage={messages.formLabels.controlVariant}
                          titleColor={theme.orangeBackground}
                          namePlaceholder={messages.formLabels.scenarioNamePlaceholderA}
                          scenarios={availableScenarios}
                          scenariosForDetails={scenarios}
                          disabledValue={sideBSelection}
                          oppositeVariant={undefined}
                          form={form}
                        />
                      </Card.Body>
                    </Card>
                  </div>

                  {/* Test Variant (Variant 1) */}
                  <div className="col-md-6">
                    <Card className="mb-3">
                      <Card.Body>
                        <ScenarioSelectCard
                          variantIndex={1}
                          titleMessage={messages.formLabels.testVariant}
                          titleColor={theme.darkBlueBackground}
                          namePlaceholder={messages.formLabels.scenarioNamePlaceholderB}
                          scenarios={availableScenarios}
                          scenariosForDetails={scenarios}
                          disabledValue={sideASelection}
                          oppositeVariant={sideBOppositeVariant}
                          disabledUntilOppositeSelected
                          form={form}
                        />
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Block 3: Duration and scope */}
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">
                  <FormattedMessage {...messages.formLabels.trafficBlock} />
                </h5>

                <div className="row">
                  <div className="col-md-4">
                    <FormGroup className="mb-3">
                      <FormLabel htmlFor="targetPathInput">
                        <FormattedMessage {...messages.formLabels.targetPath} />
                      </FormLabel>
                      <FormControl
                        id="targetPathInput"
                        type="text"
                        {...form.register("targetPath")}
                      />
                      <Form.Text muted>
                        <FormattedMessage {...messages.formLabels.targetPathHelp} />
                      </Form.Text>
                      {form.formState.errors.targetPath && (
                        <ErrorField>{form.formState.errors.targetPath.message}</ErrorField>
                      )}
                    </FormGroup>
                  </div>

                  <div className="col-md-4">
                    <FormGroup className="mb-3">
                      <FormLabel htmlFor="mdeInput">
                        <FormattedMessage {...messages.formLabels.mde} />
                      </FormLabel>
                      <FormControl
                        id="mdeInput"
                        type="number"
                        min={1}
                        max={100}
                        {...form.register("mde", { valueAsNumber: true })}
                      />
                      <Form.Text muted>
                        <FormattedMessage {...messages.formLabels.mdeHelp} />
                      </Form.Text>
                      {estimatedVisitors > 0 && (
                        <Form.Text muted data-testid="sample-size-hint">
                          <FormattedMessage
                            {...messages.formLabels.sampleHint}
                            values={{
                              visitors: intl.formatNumber(estimatedVisitors),
                              baseline: intl.formatNumber(DEFAULT_BASELINE_CONVERSION_RATE * 100, {
                                maximumFractionDigits: 1,
                              }),
                            }}
                          />
                        </Form.Text>
                      )}
                      {form.formState.errors.mde && (
                        <ErrorField>{form.formState.errors.mde.message}</ErrorField>
                      )}
                    </FormGroup>
                  </div>

                  <div className="col-md-4">
                    <FormGroup className="mb-3">
                      <FormLabel htmlFor="minDaysInput">
                        <FormattedMessage {...messages.formLabels.minDays} />
                      </FormLabel>
                      <FormControl
                        id="minDaysInput"
                        type="number"
                        min={1}
                        max={365}
                        {...form.register("minDays", { valueAsNumber: true })}
                      />
                      <Form.Text muted>
                        <FormattedMessage {...messages.formLabels.minDaysHelp} />
                      </Form.Text>
                      {form.formState.errors.minDays && (
                        <ErrorField>{form.formState.errors.minDays.message}</ErrorField>
                      )}
                    </FormGroup>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <FormGroup className="mb-3">
                      <FormLabel htmlFor="startDateInput">
                        <FormattedMessage {...messages.formLabels.startDate} />
                      </FormLabel>
                      <Controller
                        name="startedAt"
                        control={form.control}
                        render={({ field }) => (
                          <DateField
                            id="startDateInput"
                            selected={field.value ?? null}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      {isStartDateRequiredError(form.formState.errors.startedAt) ? (
                        <ErrorField>
                          {intl.formatMessage(messages.validation.startDateRequired)}
                        </ErrorField>
                      ) : (
                        isDateValidationKey(form.formState.errors.startedAt?.message) && (
                          <ErrorField>
                            {intl.formatMessage(
                              DATE_VALIDATION_MESSAGES[
                                form.formState.errors.startedAt!
                                  .message as keyof typeof DATE_VALIDATION_MESSAGES
                              ]
                            )}
                          </ErrorField>
                        )
                      )}
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup className="mb-3">
                      <FormLabel htmlFor="estimatedEndDateInput">
                        <FormattedMessage {...messages.formLabels.endDate} />
                      </FormLabel>
                      <Controller
                        name="estimatedEndDate"
                        control={form.control}
                        render={({ field }) => (
                          <DateField
                            id="estimatedEndDateInput"
                            selected={field.value ?? null}
                            onChange={field.onChange}
                            isClearable
                          />
                        )}
                      />
                      <Form.Text muted>
                        <FormattedMessage {...messages.formLabels.endDateHelp} />
                      </Form.Text>
                      {isDateValidationKey(form.formState.errors.estimatedEndDate?.message) && (
                        <ErrorField>
                          {intl.formatMessage(
                            DATE_VALIDATION_MESSAGES[
                              form.formState.errors.estimatedEndDate!
                                .message as keyof typeof DATE_VALIDATION_MESSAGES
                            ]
                          )}
                        </ErrorField>
                      )}
                    </FormGroup>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Form Actions */}
            <div className="flex gap-3 mt-4">
              <Button variant="primary" type="submit" disabled={loading}>
                <FormattedMessage {...messages.formLabels.submit} />
              </Button>
              <Button variant="secondary" onClick={handleGoBack}>
                <FormattedMessage {...messages.formLabels.cancel} />
              </Button>
            </div>
          </Form>
        </FormProvider>
      </Container>
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(ExperimentEdit);
